import { TreeViewNode, TreeView, NodeId, Delta } from '@fluid-experimental/tree';
import { logWarn } from '../../../log';
import { NodeGroup, DeltaNodeGroup, MfsChangeCategory, Match, MfsChange } from '.';
import { NodeKind } from '..';
import { MfsStatusCode } from '@metaos/mfs-telemetry';

function groupNodesByKind(view: TreeView, nodeIds: readonly NodeId[]): NodeGroup {
  return nodeIds.reduce((map: { [key: string]: TreeViewNode[] }, nodeId) => {
    const node = view.getViewNode(nodeId);
    (map[node.definition as string] ||= []).push(node);
    return map;
  }, {});
}

function getDeltaNodes(view: TreeView, previousView: TreeView, delta: Delta<NodeId>): DeltaNodeGroup {
  return {
    changed: groupNodesByKind(view, delta.changed),
    added: groupNodesByKind(view, delta.added),
    removed: groupNodesByKind(previousView, delta.removed),
  };
}

function areSameKind(nodes: NodeGroup): boolean {
  return Object.keys(nodes).length === 1;
}

export function hasAny(delta: DeltaNodeGroup, kind: NodeKind, key: keyof DeltaNodeGroup): boolean {
  return (kind as keyof NodeGroup) in delta[key];
}

export function hasOnly(delta: DeltaNodeGroup, kind: NodeKind, key: keyof DeltaNodeGroup): boolean {
  return (kind as keyof NodeGroup) in delta[key] && areSameKind(delta[key]);
}

export function hasNone(delta: DeltaNodeGroup, key: keyof DeltaNodeGroup): boolean {
  return Object.keys(delta[key]).length === 0;
}

function isItemsChanged(view: TreeView, delta: Delta<NodeId>): boolean {
  return delta.changed.length === 1 && delta.changed[0] === view.root;
}

export function isEmptyDelta<T>(delta: Delta<T>): boolean {
  return delta.added.length === 0 && delta.removed.length === 0 && delta.changed.length === 0;
}

export function getNodes(
  delta: DeltaNodeGroup,
  changeKind: 'add' | 'remove' | 'update',
  kind: NodeKind
): readonly TreeViewNode[] {
  return (
    (changeKind === 'add' ? delta.added[kind] : changeKind === 'remove' ? delta.removed[kind] : delta.changed[kind]) ??
    []
  );
}

/** Gets the node at `index` from the `delta` if it's of the expected `kind` and `type`. */
export function getNode<T extends TreeViewNode>(
  index: number,
  delta: DeltaNodeGroup,
  kind: NodeKind,
  guard: (node: TreeViewNode) => node is T
): Match<T> {
  const maybeNode = (delta.changed[kind] ?? [])[index];
  return !!maybeNode && guard(maybeNode) ? Match.ok(maybeNode) : Match.fail();
}

function isCollectionChanged(delta: DeltaNodeGroup): boolean {
  if (hasAny(delta, NodeKind.Item, 'changed') || hasAny(delta, NodeKind.Reference, 'changed')) {
    if (hasNone(delta, 'added') && hasNone(delta, 'removed')) {
      // likely a move
      return true;
    }

    if (hasAny(delta, NodeKind.Reference, 'added') || hasAny(delta, NodeKind.Reference, 'removed')) {
      // likely add/remove/clear elements
      return true;
    }
  }
  return false;
}

function isPropertyChanged(delta: DeltaNodeGroup): boolean {
  if (hasOnly(delta, NodeKind.Item, 'changed') || hasOnly(delta, NodeKind.Array, 'changed')) {
    if (
      hasAny(delta, NodeKind.SingleValueNode, 'added') ||
      hasAny(delta, NodeKind.SingleValueNode, 'removed') ||
      hasAny(delta, NodeKind.MultiValueNode, 'added') ||
      hasAny(delta, NodeKind.MultiValueNode, 'removed') ||
      hasAny(delta, NodeKind.Array, 'added') ||
      hasAny(delta, NodeKind.Array, 'removed')
    ) {
      return true;
    }
  }
  return false;
}

function isRelationshipChanged(delta: DeltaNodeGroup): boolean {
  if (hasAny(delta, NodeKind.EdgeSet, 'changed') || hasAny(delta, NodeKind.Item, 'changed')) {
    if (
      hasAny(delta, NodeKind.Edge, 'added') ||
      hasAny(delta, NodeKind.Edge, 'removed') ||
      hasAny(delta, NodeKind.EdgeSet, 'added') ||
      hasAny(delta, NodeKind.EdgeSet, 'removed')
    ) {
      return true;
    }
  }
  return false;
}

export function getChangeCategory(
  view: TreeView,
  previousView: TreeView,
  deltaNodeIds: Delta<NodeId>
): { delta: DeltaNodeGroup; category: MfsChangeCategory } {
  if (isItemsChanged(view, deltaNodeIds)) {
    // changed is always initial tree so we can omit it from processing.
    const { added, removed } = deltaNodeIds;
    return {
      delta: getDeltaNodes(view, previousView, { changed: [], added, removed }),
      category: MfsChangeCategory.Items,
    };
  }

  const delta = getDeltaNodes(view, previousView, deltaNodeIds);

  if (isCollectionChanged(delta)) {
    return { delta, category: MfsChangeCategory.Collection };
  }

  if (isPropertyChanged(delta)) {
    return { delta, category: MfsChangeCategory.Property };
  }

  if (isRelationshipChanged(delta)) {
    return { delta, category: MfsChangeCategory.Relationship };
  }

  return { delta, category: MfsChangeCategory.Unknown };
}

export function matchCategoryFailed(
  _view: TreeView,
  _previousView: TreeView,
  delta: DeltaNodeGroup
): Match<MfsChange[]> {
  logWarn('onChange', MfsStatusCode.ExpectationFailed, { message: `Failed to categorize changes delta` });
  return Match.fail();
}
